/* global HSCore */

import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import Footer from "../Footer";
import LanguageContext from "../../utils/Contexts/LanguageContext";
import _ from "lodash";
import UserContext from "../../utils/Contexts/UserContext";
import axiosInstance from "../../utils/Requests/axios";
import toast from "react-hot-toast";
import TablePagination from "../Common/Pagination";
import ArticalTable from "./ArticalTable";
import ThemeContext from "../../utils/Contexts/ThemeContext";
import { useNavigate } from "react-router-dom";

import "./style.css";

const Articles = () => {
  const { t } = useContext(LanguageContext);
  const navigate = useNavigate();
  const { userData } = useContext(UserContext);
  const { selectedTheme } = useContext(ThemeContext);
  const [pageCount, setPageCount] = useState(15);
  const [activePage, setActivePage] = useState(1);
  const [columns, setColumns] = useState({
    title: true,
    contract: true,
    status: true,
    created: true,
  });

  const handleToggle = (column) => {
    setColumns((prevColumns) => ({
      ...prevColumns,
      [column]: !prevColumns[column],
    }));
  };

  const [columnSorts, setColumnSorts] = useState({
    0: null,
    1: null,
    2: "desc",
  });
  const [tableData, setTableData] = useState(null);
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [recordsFiltered, setRecordsFiltered] = useState(0);
  const [formData, setFormData] = useState({
    draw: 1,
    columns: [{ data: "title" }, { data: "status" }, { data: "created" }],
    order: [{ column: "2", dir: "desc" }],
    start: 0,
    length: 15,
    search: {
      value: "",
      filters: [],
    },
  });

  useEffect(() => {
    const payload = { ...formData };
    const start = activePage === 1 ? 0 : (activePage - 1) * pageCount;
    if (payload.start !== start || payload.length !== pageCount) {
      payload.start = start;
      payload.length = pageCount;
      setFormData(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, pageCount]);

  const fetchArticles = () => {
    const payload = _.cloneDeep(formData);
    const url = `users/${userData?.uuid}/articles/search`;
    axiosInstance
      .post(url, payload)
      .then((response) => {
        const data = response?.data?.data || [];
        const filtered = response?.data?.recordsFiltered || 0;
        setTableData(data);
        setRecordsFiltered(filtered);
      })
      .catch(() => {
        toast.error(t("There was an error fetching backlinks offers"));
      });
  };

  useEffect(() => {
    const payload = { ...formData };
    const order = [];
    Object.keys(columnSorts).forEach((columnIndex) => {
      if (columnSorts[columnIndex]) {
        order.push({
          column: columnIndex,
          dir: columnSorts[columnIndex],
        });
      }
    });
    if (order.length) {
      payload.order = order;
      setFormData(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnSorts]);

  const previousPayloadRef = useRef(null);
  useEffect(() => {
    const payload = { ...formData };
    if (!_.isEqual(previousPayloadRef.current, payload)) {
      previousPayloadRef.current = payload;
      fetchArticles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      search: {
        ...prevData.search,
        value: searchTerm,
      },
    }));
  }, [searchTerm]);

  // eslint-disable-next-line
  const debouncedSearch = useCallback(
    _.debounce((searchTerm) => {
      setActivePage(1);
      setSearchTerm(searchTerm);
    }, 500),
    []
  );

  useEffect(() => {
    debouncedSearch(search);
    return () => {
      debouncedSearch.cancel();
    };
  }, [search, debouncedSearch]);

  const hsInitRef = useRef(null);
  useEffect(() => {
    if (!hsInitRef.current) {
      HSCore.components.HSTomSelect.init(".js-select-search");
      // eslint-disable-next-line react-hooks/exhaustive-deps
      hsInitRef.current = true;
    }
  }, []);

  return (
    <main id="content" role="main" className="main">
      <div className="content container-fluid">
        <div
          className="page-header d-flex justify-content-between align-items-center mb-0 pb-1"
          style={{ borderBottom: "unset" }}
        >
          <div className="row align-items-center mb-3">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb breadcrumb-no-gutter">
                <li className="breadcrumb-item">
                  {/* eslint-disable-next-line */}
                  <a href="javascript:;"
                    className="breadcrumb-link"
                    style={{ opacity: "0.6" }}
                  >
                    <span
                      style={{
                        color: "#919293",
                        fontSize: "18px",
                        fontWeight: "400",
                      }}
                    >
                      {" "}
                      {t("Content")} / {t("Articles")}
                    </span>
                  </a>
                </li>
              </ol>
            </nav>

            <div className="col-md d-md-flex justify-content-between align-items-center mb-sm-2 mb-2 mb-md-0">
              <h1
                className="page-header-title"
                style={{ fontSize: "28px", fontWeight: "700" }}
              >
                {t("Articles")}
              </h1>
            </div>
          </div>
          <button
            onClick={() => navigate(`/content/editor`)}
            className="btn btn-primary btn-sm d-flex justify-content-center align-items-center gap-2"
            style={{
              borderRadius: "10px",
              width: "151px",
              fontSize: "16px",
              fontWeight: "600",
              height: "47px",
              backgroundColor: "#387CFF",
            }}
          >
            {t("Add Article")} <i className="bi bi-plus fs-1"></i>
          </button>
        </div>
        <div
          className="mb-4"
          style={{
            borderBottom: `.0625rem solid ${
              selectedTheme === "dark" ? "#2f3235" : "rgba(231,234,243,.7)"
            }`,
            marginLeft: "-30px",
            marginRight: "-30px",
          }}
        ></div>
        <div className="card">
          <div className="card-header card-header-content-between">
            <div className="col-6">
              <div className="input-group input-group-merge input-group-flush border rounded-2 article-search-input pe-2">
                <div className="input-group-prepend input-group-text px-2">
                  <i className="bi-search article-search-icon"></i>
                </div>
                <input
                  id="datatableSearch"
                  type="search"
                  className="form-control custom-placeholder article-search-input"
                  placeholder={t("Search Articles")}
                  aria-label="Search Articles"
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="col-6 d-flex justify-content-end align-items-center gap-2">
              <div className="dropdown article-column-dropdown">
                <button
                  type="button"
                  className="btn btn-white w-100 text-nowrap text-secondary"
                  id="showHideDropdown"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  data-bs-auto-close="outside"
                >
                  <i className="bi bi-layout-three-columns me-1 text-secondary"></i>{" "}
                  {t("Columns")}
                </button>

                <div
                  className="dropdown-menu dropdown-menu-end dropdown-card"
                  aria-labelledby="showHideDropdown"
                  style={{ width: "15rem" }}
                >
                  <div className="card card-sm">
                    <div className="card-body">
                      <div className="d-grid gap-3">
                        {Object.keys(columns).map((key) => (
                          <label
                            key={key}
                            className="row form-check form-switch"
                            htmlFor={`toggleColumn_${key}`}
                          >
                            <span className="col-8 col-sm-9 ms-0">
                              <span className="me-2">
                                {t(_.startCase(key))}
                              </span>
                            </span>
                            <span className="col-4 col-sm-3 text-end">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`toggleColumn_${key}`}
                                checked={columns[key]}
                                onChange={() => handleToggle(key)}
                              />
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <ArticalTable
            data={tableData}
            columnsVisibility={columns}
            setColumnSorts={setColumnSorts}
            columnSorts={columnSorts}
          />

          <TablePagination
            activePage={activePage}
            setActivePage={setActivePage}
            recordsFiltered={recordsFiltered}
            setPageCount={setPageCount}
            pageCount={pageCount}
          />
        </div>
      </div>
      <Footer />
    </main>
  );
};

export default Articles;
